import { IconButton } from '@creditornot/cb-button';
import { Cross } from '@creditornot/cb-icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Asset, AssetEnhancement } from 'apis/asset-repository-service/types';

import { ImageContainer, ImageFrame, ImageName, cardShimmerStyle } from './ImageCardStyles';
import { LazyImage } from './LazyImage';

type Props = {
  id: Asset['id'];
  src: string;
  name?: string;
  imageEnhancement?: AssetEnhancement;
  loading?: boolean;
  thumbnail?: boolean;
  active?: boolean;
  deleteDisabled?: boolean;
  onClick?: () => void;
  onDelete?: (id: Asset['id']) => void;
};

const DeleteButton = styled(IconButton)`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  background-color: ${color.bgFillInverse};
  display: none;
`;

const StyledImageContainer = styled(ImageContainer)<{ $active: boolean; $thumbnail: boolean }>`
  flex-shrink: 0;
  height: ${({ $thumbnail }) => ($thumbnail ? '57px' : 'auto')};
  outline: 2px solid ${({ $active }) => ($active ? color.borderSelected : 'transparent')};

  &:hover ${DeleteButton} {
    display: block;
  }
`;

const ImageFrames = styled.div<{ $loading: boolean }>`
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 532px;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  transition: opacity 800ms;

  ${cardShimmerStyle};
  &::after {
    /* fade out shimmer effect */
    opacity: ${({ $loading }) => ($loading ? 1 : 0)};
    transition: opacity 1.2s;
  }
`;

const ImageEnhancementFrame = styled(ImageFrame)<{ $hide: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: #eee;
  opacity: ${({ $hide }) => ($hide ? 0 : 1)};
  transition: opacity 600ms;
`;

export const ImageCardWithEnhancement = ({
  id,
  src,
  name,
  imageEnhancement,
  loading = false,
  thumbnail = false,
  active = false,
  deleteDisabled = false,
  onClick,
  onDelete,
}: Props) => {
  const [isEnhancedImageLoaded, setIsEnhancedImageLoaded] = React.useState(false);
  const isLoadingImageEnhancement = loading || (Boolean(imageEnhancement) && !isEnhancedImageLoaded);

  useEffect(() => {
    if (!imageEnhancement) {
      setIsEnhancedImageLoaded(false);
    }
  }, [src, imageEnhancement]);

  return (
    <StyledImageContainer onClick={onClick} $active={active} $thumbnail={thumbnail} $clickable={Boolean(onClick)}>
      {onDelete && (
        <DeleteButton variant="subdued" size="tiny" onClick={() => onDelete(id)} disabled={deleteDisabled}>
          <Cross />
        </DeleteButton>
      )}
      <ImageFrames $loading={isLoadingImageEnhancement}>
        <ImageFrame>
          <LazyImage src={src} alt={name || src} />
        </ImageFrame>
        {imageEnhancement && (
          <ImageEnhancementFrame $hide={isLoadingImageEnhancement}>
            <LazyImage
              src={imageEnhancement.url}
              alt={name || src}
              onLoad={() => {
                setIsEnhancedImageLoaded(true);
              }}
            />
          </ImageEnhancementFrame>
        )}
      </ImageFrames>
      {name && <ImageName title={name}>{name}</ImageName>}
    </StyledImageContainer>
  );
};
